import React from "react";
import { TrendingUp, TrendingDown, DollarSign, ArrowUpDown } from "lucide-react";
import { TableRow } from "./TableRow";
import { TransactionRow } from "./TransactionRow";

interface ReportingPeriod {
  reportingPeriodId: string;
  period: string;
}

interface Fund {
  name: string;
}

interface UnifiedTransaction {
  key: string;
  transactionType: string;
  instrumentName: string;
  current?: {
    baseAmount: number;
  };
  comparison?: {
    baseAmount: number;
  };
}

interface CategoryData {
  name: string;
  currentTotal: number;
  comparisonTotal: number;
  unifiedTransactions: UnifiedTransaction[];
}

interface GroupedData {
  currentTotal: number;
  comparisonTotal: number;
  unified: Record<string, CategoryData>;
}

interface ExplainerTableProps {
  selectedFund: Fund | null;
  selectedPeriod: ReportingPeriod | null;
  comparisonPeriod: ReportingPeriod | null;
  groupedData: GroupedData;
  expandedCategories: Set<string>;
  toggleCategory: (categoryKey: string) => void;
  getCategoryBasedValueColor: (value: number, category?: string) => string;
}

export const ExplainerTable: React.FC<ExplainerTableProps> = ({
  selectedFund,
  selectedPeriod,
  comparisonPeriod,
  groupedData,
  expandedCategories,
  toggleCategory,
  getCategoryBasedValueColor,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b-2 border-gray-200">
            <th className="text-left py-4 px-4 font-semibold">Name</th>
            <th className="text-right py-4 px-4 font-semibold">
              Current Period
              {selectedPeriod && (
                <div className="text-sm font-normal text-gray-500">
                  {new Date(selectedPeriod.period).toLocaleDateString()}
                </div>
              )}
            </th>
            <th className="text-right py-4 px-4 font-semibold">
              Comparison Period
              <div className="text-sm font-normal text-gray-500">
                {comparisonPeriod ? 
                  new Date(comparisonPeriod.period).toLocaleDateString() :
                  "Previous Period"
                }
              </div>
            </th>
            <th className="text-right py-4 px-4 font-semibold">
              Change
              <div className="text-sm font-normal text-gray-500">
                Difference
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Fund Level */}
          <TableRow
            level={0}
            name={selectedFund?.name || "Fund"}
            currentValue={groupedData.currentTotal}
            comparisonValue={groupedData.comparisonTotal}
            icon={<DollarSign className="h-5 w-5 text-blue-600" />}
            getCategoryBasedValueColor={getCategoryBasedValueColor}
          />

          {/* Category Level */}
          {Object.entries(groupedData.unified).map(([categoryKey, categoryData]) => {
            const isExpanded = expandedCategories.has(categoryKey);
            
            if (categoryData.unifiedTransactions.length === 0) return null;

            return (
              <React.Fragment key={categoryKey}>
                <TableRow
                  level={1}
                  name={categoryData.name}
                  currentValue={categoryData.currentTotal}
                  comparisonValue={categoryData.comparisonTotal}
                  isExpandable={true}
                  isExpanded={isExpanded}
                  onToggle={() => toggleCategory(categoryKey)}
                  transactionCount={categoryData.unifiedTransactions.length}
                  icon={
                    categoryKey === 'income' ? 
                      <TrendingUp className="h-4 w-4 text-emerald-600" /> : 
                    categoryKey === 'expenses' ? 
                      <TrendingDown className="h-4 w-4 text-red-600" /> :
                    categoryKey === 'cash' ? 
                      <DollarSign className="h-4 w-4 text-blue-600" /> :
                      <ArrowUpDown className="h-4 w-4 text-purple-600" />
                  }
                  categoryKey={categoryKey}
                  getCategoryBasedValueColor={getCategoryBasedValueColor}
                />

                {/* Transactions Level */}
                {isExpanded && categoryData.unifiedTransactions.map((unifiedTransaction) => (
                  <TransactionRow
                    key={unifiedTransaction.key}
                    unifiedTransaction={unifiedTransaction}
                    categoryKey={categoryKey}
                    getCategoryBasedValueColor={getCategoryBasedValueColor}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}; 